import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileDown, Loader2, Search, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/common";
import { api, download } from "@/lib/api";
import { inr, fmtDateTime } from "@/lib/format";
import { toast } from "sonner";

function SlipButton({ month, code, label, testid, primary }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await download(`/payroll/runs/${month}/payslips`, code ? { code } : {}); } catch (e) { toast.error("Download failed"); } finally { setBusy(false); }
  };
  return (
    <Button size={primary ? "default" : "sm"} variant={primary ? "default" : "outline"} className={primary ? "bg-blue-800 hover:bg-blue-900" : ""} onClick={go} disabled={busy} data-testid={testid}>
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} {label}
    </Button>
  );
}

export default function Payslips() {
  const [runs, setRuns] = useState(null);
  const [month, setMonth] = useState("");
  const [run, setRun] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/payroll/runs").then((r) => {
      const fin = r.data.filter((x) => x.status === "finalized");
      setRuns(fin);
      if (fin.length) setMonth(fin[0].month);
    }).catch(() => setRuns([]));
  }, []);
  useEffect(() => {
    if (!month) { return; }
    setRun(null);
    api.get(`/payroll/runs/${month}`).then((r) => setRun(r.data)).catch(() => toast.error("Could not load payroll"));
  }, [month]);

  const ql = q.trim().toLowerCase();
  const rows = (run?.rows || []).filter((r) => !ql || r.code.toLowerCase().includes(ql) || r.name.toLowerCase().includes(ql));

  if (runs === null) return <div className="py-16 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-blue-800" /></div>;
  return (
    <div className="space-y-5">
      <PageHeader title="Payslips" subtitle="Generate printable PDF salary slips from a finalized payroll: all employees in one file (one slip per page) or one employee at a time." />
      {runs.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-10 text-center" data-testid="payslips-empty">
          <Receipt className="h-8 w-8 text-slate-300 mx-auto" />
          <div className="mt-3 text-slate-600">No finalized payroll yet. Payslips can be generated once a month is finalized.</div>
          <Link to="/finalize-payroll" className="inline-block mt-3 text-sm font-semibold text-blue-800 hover:underline" data-testid="payslips-go-finalize">Go to Finalize Payroll</Link>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col lg:flex-row lg:items-end justify-between gap-4">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <div className="text-xs font-semibold text-slate-500 mb-1.5">Finalized payroll month</div>
                <Select value={month} onValueChange={setMonth}>
                  <SelectTrigger className="w-56" data-testid="payslips-month-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{runs.map((r) => <SelectItem key={r.month} value={r.month} data-testid={`payslips-month-${r.month}`}>{r.month_label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {run && <div className="text-sm text-slate-500" data-testid="payslips-run-meta">{run.employees} employees · Net payable <b className="text-blue-800 money">{inr(run.totals.final)}</b> · Finalized {fmtDateTime(run.finalized_at)}</div>}
            </div>
            {run && <SlipButton month={month} primary label={`Download All Payslips (${run.employees})`} testid="payslips-download-all" />}
          </div>
          <div className="bg-white rounded-xl border border-slate-200">
            <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
              <span className="font-heading font-semibold text-slate-800">Employees</span>
              <div className="relative w-full sm:w-64"><Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ID or name" className="pl-9 h-9" data-testid="payslips-search" /></div>
            </div>
            {!run ? <div className="py-10 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-blue-800" /></div> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="payslips-table">
                  <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                    <tr>{["Employee ID", "Employee Name", "Earned Salary", "Deduction", "Net Payable", ""].map((h, i) => <th key={h || i} className={`px-4 py-2.5 ${i >= 2 && i <= 4 ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 && <tr><td colSpan={6} className="text-center text-slate-500 py-8">No employees match.</td></tr>}
                    {rows.map((r) => (
                      <tr key={r.code} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`payslip-row-${r.code}`}>
                        <td className="px-4 py-2.5 font-semibold text-blue-800 money">{r.code}</td>
                        <td className="px-4 py-2.5">{r.name}</td>
                        <td className="px-4 py-2.5 text-right money">{inr(r.earned)}</td>
                        <td className="px-4 py-2.5 text-right money text-red-700">{inr(r.deduction)}</td>
                        <td className="px-4 py-2.5 text-right money font-bold">{inr(r.final)}</td>
                        <td className="px-4 py-2.5 text-right"><SlipButton month={month} code={r.code} label="Payslip" testid={`payslip-download-${r.code}`} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
